import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

// FY24 rebrand type matrix rules as per https://www.figma.com/file/RZRs55BkCWtX6zBPUD7dRb/Type-Matrix-React---FY24-Updates?type=design&node-id=323-741&mode=design&t=7RUZPtxLZpLvX1Q5-0
// colors
const PRIMARY_BLACK = "#101214";
const NEUTRAL_LIGHT = "#292A2E";

const typographyStyles = css({
    // headlines
    "h1, .h1": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 800,
        color: `${PRIMARY_BLACK}`,
        fontSize: "2.5rem",
        lineHeight: "2.875rem",

        [media.only.sm]: {
            fontSize: "2.75rem",
            lineHeight: "3.125rem"
        },
        [media.above.md]: {
            fontSize: "3rem",
            lineHeight: "3.5rem"
        }
    },
    "h2, .h2": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "2rem",
        lineHeight: "2.375rem",

        [media.only.sm]: {
            fontSize: "2.25rem",
            lineHeight: "2.625rem"
        },
        [media.above.md]: {
            fontSize: "2.5rem",
            lineHeight: "3rem"
        }
    },
    "h3, .h3": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "1.5rem",
        lineHeight: "1.875rem",

        [media.only.sm]: {
            fontSize: "1.75rem",
            lineHeight: "2.125rem"
        },
        [media.above.md]: {
            fontSize: "2rem",
            lineHeight: "2.375rem"
        }
    },
    "h4, .h4": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 700,
        color: `${PRIMARY_BLACK}`,
        fontSize: "1.25rem",
        lineHeight: "1.625rem",

        [media.only.sm]: {
            fontSize: "1.375rem",
            lineHeight: "1.75rem"
        },
        [media.above.md]: {
            fontSize: "1.5rem",
            lineHeight: "1.875rem"
        }
    },
    "h5, .h5": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 700,
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
        color: `${PRIMARY_BLACK}`,

        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.75rem"
        }
    },
    "h6, .h6": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        color: `${PRIMARY_BLACK}`
    },

    // body
    ".body-lg": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "1.125rem",
        lineHeight: "1.625rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.75rem"
        }
    },
    "p, .body-md": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5rem",
        color: "#292A2E"
    },
    ".body-sm": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.25rem",
        color: "#292A2E"
    },

    // additional styles
    ".superhero, .superhero-contrast": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontSize: "3.125rem",
        lineHeight: "3.75rem",
        color: `${PRIMARY_BLACK}`,

        [media.only.sm]: {
            fontSize: "3.75rem",
            lineHeight: "4.375rem"
        },
        [media.above.md]: {
            fontSize: "4.375rem",
            lineHeight: "5rem"
        }
    },
    ".superhero": {
        fontWeight: 800
    },
    ".superhero-contrast": {
        fontWeight: 400
    },

    // card titles
    ".card-title": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontSize: "1.25rem",
        fontWeight: 700,
        lineHeight: "1.625rem",
        color: `${PRIMARY_BLACK}`,

        [media.only.sm]: {
            fontSize: "1.375rem",
            lineHeight: "1.75rem"
        },
        [media.above.md]: {
            fontSize: "1.5rem",
            lineHeight: "1.875rem"
        }
    },

    // captions
    ".fine-print": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1.125rem",
        color: "#292A2E"
    },
    ".fine-print--sm": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "0.625rem",
        lineHeight: "1rem",
        color: "#292A2E"
    },

    // links
    "a, .link": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontSize: "1rem",
        lineHeight: "1.5rem",
        fontWeight: 600,
        color: "#292A2E"
    },

    // eyebrow
    ".eyebrow-md": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "0.875rem",
        lineHeight: "1.125rem",
        textTransform: "uppercase",
        color: "#292A2E"
    },
    ".eyebrow-sm": {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontWeight: 400,
        fontSize: "0.75rem",
        lineHeight: "1rem",
        color: "#292A2E"
    },

    // callouts
    ".callout": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 300,
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.only.sm]: {
            fontSize: "1.5rem",
            lineHeight: "2rem"
        },
        [media.above.md]: {
            fontWeight: 400,
            fontSize: "1.75rem",
            lineHeight: "2.5rem"
        }
    },
    ".callout-lg": {
        fontFamily: "var(--bxp-charlie-display-font-family)",
        fontWeight: 300,
        fontSize: "1.75rem",
        lineHeight: "2.5rem",
        color: `${NEUTRAL_LIGHT}`,

        [media.only.sm]: {
            fontSize: "2rem",
            lineHeight: "2.75rem"
        },
        [media.above.md]: {
            fontWeight: 400,
            fontSize: "2.25rem",
            lineHeight: "3rem"
        }
    }
});

/**
 *
 * @description wrapper component used to add global font styles
 */
export const TypographyStyles = ({ children }: PropsWithChildren) => {
    return <div css={typographyStyles}>{children}</div>;
};
